import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { allGeoAreasSelector, createGeoAreaByIdSelector } from '@/redux/modules/geoArea/geoArea.selectors';
import { GeoArea } from '@/types/GeoArea';

import { findGeoAreaFromUrlParams, getLegacyGeoAreaIdFromUrlParams } from '../helpers/geoAreaToUrlParams';
import { AuctionsNearMeRouteParams } from '../types/AuctionsNearMe.types';

type UseGeoAreaFromUrlReturn = {
    geoArea: GeoArea | undefined;
    hasNewUrlParams: boolean;
    hasOldUrlParams: boolean;
    routeParams: AuctionsNearMeRouteParams;
};

export const useGeoAreaFromUrl = (): UseGeoAreaFromUrlReturn => {
    const routeParams = useParams<AuctionsNearMeRouteParams>();
    const allGeoAreas = useAppSelector(allGeoAreasSelector);

    // Derive URL format flags
    const hasNewUrlParams = Boolean(
        ('city' in routeParams && routeParams.city) ||
        ('country' in routeParams && routeParams.country) ||
        ('state' in routeParams && routeParams.state)
    );
    // Legacy links are /{geoAreaId}/{geoAreaName}/, which matches the same /:state/:city/ route as a
    // modern city URL — no route declares a `geoAreaId` param, so the id has to be read off `state`.
    const legacyGeoAreaId = getLegacyGeoAreaIdFromUrlParams({
        city: 'city' in routeParams ? routeParams.city : undefined,
        state: 'state' in routeParams ? routeParams.state : undefined,
    });
    const hasOldUrlParams = Boolean(legacyGeoAreaId);

    // Resolve geoArea from URL params. The selector is memoized per id — building it inline on every
    // render would hand reselect a fresh instance each time and defeat its cache.
    const geoAreaByIdSelector = useMemo(() => createGeoAreaByIdSelector(legacyGeoAreaId ?? 0), [legacyGeoAreaId]);
    const geoAreaFromOldUrl = useAppSelector(geoAreaByIdSelector);
    const geoAreaFromNewUrl = hasNewUrlParams
        ? findGeoAreaFromUrlParams(allGeoAreas, {
              city: 'city' in routeParams ? routeParams.city : undefined,
              country: 'country' in routeParams ? routeParams.country : undefined,
              state: 'state' in routeParams ? routeParams.state : undefined,
          })
        : undefined;
    // Legacy wins: those URLs also satisfy `hasNewUrlParams`, but their segments are an id and a
    // name, so the new-format lookup can never match them.
    const geoArea = hasOldUrlParams ? geoAreaFromOldUrl : geoAreaFromNewUrl;

    return {
        geoArea,
        hasNewUrlParams,
        hasOldUrlParams,
        routeParams,
    };
};
